import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Send } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { createCheckInRequest } from '../lib/instantData'
import GlassCard from '../components/ui/GlassCard'
import GradientButton from '../components/ui/GradientButton'
import PanelShell from '../components/ui/PanelShell'

const TOPIC_OPTIONS = [
  'Training or exercise adjustments',
  'Nutrition and meal planning',
  'Progress, results, or plateaus',
  'Recovery, motivation, or consistency',
  'Something else',
]

const RESPONSE_OPTIONS = [
  'Private dashboard message',
  'Email',
  'Request a phone call',
  'Request a video call',
]

const RequestCheckIn: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [topic, setTopic] = useState('')
  const [message, setMessage] = useState('')
  const [preferredResponse, setPreferredResponse] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async () => {
    if (!topic || !preferredResponse || !message.trim()) {
      setErrorMessage('Please fill out all fields before submitting.')
      return
    }
    setLoading(true)
    setErrorMessage('')
    try {
      await createCheckInRequest({
        userId: user!.id,
        clientFullName: user?.user_metadata?.full_name || 'Client',
        clientEmail: user?.email || '',
        topic,
        message: message.trim(),
        preferredResponse,
      })
      setSubmitted(true)
    } catch (error) {
      setErrorMessage('Something went wrong submitting your request. Please try again.')
    }
    setLoading(false)
  }

  if (submitted) {
    return (
      <PanelShell
        eyebrow="Coaching support"
        title="Request sent"
        subtitle="Cassie will review your request and get back to you soon."
        actions={
          <GradientButton onClick={() => navigate('/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
            Back to dashboard
          </GradientButton>
        }
      >
        <GlassCard hover={false}>
          <p className="text-gray-700">
            Thanks! Your check-in request has been sent. You'll hear back from Cassie based on how you asked to be contacted.
          </p>
        </GlassCard>
      </PanelShell>
    )
  }

  return (
    <PanelShell
      eyebrow="Coaching support"
      title="Request a Coaching Check-In"
      subtitle="Have a question, concern, or need some guidance? Tell Cassie what you'd like help with."
      actions={
        <GradientButton onClick={() => navigate('/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
          Back to dashboard
        </GradientButton>
      }
    >
      <GlassCard hover={false}>
        <div className="space-y-6">
          {errorMessage && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {errorMessage}
            </div>
          )}

          <div>
            <label className="form-label">What would you like help with?</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {TOPIC_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => setTopic(option)}
                  className={`p-3 rounded-lg text-sm font-medium text-left transition-all duration-200 border border-[#2c2c2c] ${
                    topic === option
                      ? 'bg-ink text-white border-ink'
                      : 'bg-white text-gray-600 hover:bg-brand-soft border-gray-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="form-label">Tell Cassie more</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Share as much detail as you'd like..."
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">How would you prefer Cassie to respond?</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {RESPONSE_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => setPreferredResponse(option)}
                  className={`p-3 rounded-lg text-sm font-medium text-left transition-all duration-200 border border-[#2c2c2c] ${
                    preferredResponse === option
                      ? 'bg-ink text-white border-ink'
                      : 'bg-white text-gray-600 hover:bg-brand-soft border-gray-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Phone and video requests are subject to your coaching plan and Cassie's availability.
            </p>
          </div>

          <div className="flex justify-end">
            <GradientButton onClick={handleSubmit} disabled={loading} icon={Send}>
              {loading ? 'Sending...' : 'Send Request'}
            </GradientButton>
          </div>
        </div>
      </GlassCard>
    </PanelShell>
  )
}

export default RequestCheckIn
